import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';

export type CustomAmountRangeNotice = 'below' | 'above' | null;

interface UseCustomAmountInputParams {
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  updateClient?: (value: number) => void;
}

function noticeFor(candidate: number, min: number, max: number): CustomAmountRangeNotice {
  if (candidate < min) return 'below';
  if (candidate > max) return 'above';
  return null;
}

/**
 * Drives the numeric "Custom Amount" field for CustomSlider.
 *
 * `value` is the value currently on display — the live drag value while the thumb is
 * moving, otherwise the committed form value — so the field and the slider never show
 * different numbers.
 *
 * The draft string only holds keystrokes while the field is focused, and can never push
 * a number into the form that differs from the one on screen: input outside [min, max]
 * is not committed, it raises a range notice instead. Clamping happens on blur, never
 * per keystroke.
 */
export function useCustomAmountInput({
  value,
  min,
  max,
  onChange,
  updateClient,
}: UseCustomAmountInputParams) {
  const [draft, setDraft] = useState<string>(() => String(value ?? ''));
  const [isFocused, setIsFocused] = useState(false);
  const [rangeNotice, setRangeNotice] = useState<CustomAmountRangeNotice>(null);
  // Blur commits a clamped value, which immediately re-enters through the sync effect
  // below. Without this the effect would wipe the notice that explains the clamp.
  const preserveNoticeRef = useRef(false);

  // Mirrors the displayed value into the field whenever the user is not typing, which
  // is what keeps the field in step with a slider drag.
  useEffect(() => {
    if (!isFocused && typeof value === 'number' && !isNaN(value)) {
      setDraft(String(value));
      if (preserveNoticeRef.current) {
        preserveNoticeRef.current = false;
      } else {
        setRangeNotice(null);
      }
    }
  }, [value, isFocused]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const nextDraft = event.target.value;
      setDraft(nextDraft);

      if (nextDraft === '') {
        setRangeNotice(null);
        return;
      }

      const parsed = Number(nextDraft);
      if (!Number.isFinite(parsed)) {
        setRangeNotice(null);
        return;
      }

      const notice = noticeFor(parsed, min, max);
      setRangeNotice(notice);

      if (!notice) {
        onChange(parsed);
      }
    },
    [max, min, onChange],
  );

  const handleBlur = useCallback(() => {
    setIsFocused(false);

    const parsed = draft === '' ? value : Number(draft);
    const resolved = Number.isFinite(parsed) ? parsed : value;
    const clamped = Math.max(min, Math.min(max, resolved));

    // Keep the notice up after clamping, so a corrected value does not silently
    // replace what the user typed.
    const notice = noticeFor(resolved, min, max);
    preserveNoticeRef.current = notice !== null;
    setRangeNotice(notice);
    setDraft(String(clamped));
    onChange(clamped);
    updateClient?.(clamped);
  }, [draft, max, min, onChange, updateClient, value]);

  const handleFocus = useCallback(() => setIsFocused(true), []);

  return { draft, rangeNotice, handleChange, handleBlur, handleFocus };
}
